/* eslint-disable import/no-unresolved, import/named, import/no-extraneous-dependencies, import/no-duplicates, import/order, import/no-self-import,  import/no-relative-packages  */
import React, { useState, useEffect, useRef } from "react";
import { FacebookShareButton, LinkedinShareButton, TwitterShareButton } from "react-share";
import { getMicrocopyValueFromKeys, getSiteURL, getThemeSettings } from "@qlik/qlik/src/lib/util/helpers";
import { Tooltip as ReactTooltip } from "react-tooltip";
import * as styles from "./styles.module.css";

// Icons for sharing components
import CopySVG from "@qlik/qlik/src/assets/images/link-icon.inline.svg";
import FacebookSVG from "@qlik/qlik/src/assets/images/sm-fb.inline.svg";
import LinkedinSVG from "@qlik/qlik/src/assets/images/sm-linkedin.inline.svg";
import TwitterSVG from "@qlik/qlik/src/assets/images/sm-twitter.inline.svg";
import { COPY_LINK_BUTTON_TOOLTIP_TEXT } from "@qlik/qlik/src/lib/constants/blog-constants";

const SocialShare = ({
    pageTitle = "",
    pageSlug = "",
    startElementSelector,
    stopElementSelector,
    startElementPosition = "bottom",
    stopElementPosition = "top",
    additionalClasses = "", // Custom utility classes to be applied
}) => {
    const [isVisible, setIsVisible] = useState(false);
    const [gapCalculated, setGapCalculated] = useState(false);
    const [gapPixels, setGapPixels] = useState(0);
    const commonSocialShareRef = useRef(null);

    useEffect(() => {
        // Function to calculate the gap between elements
        const calculateGap = () => {
            const startElement = document.querySelector(startElementSelector);
            const stopElement = document.querySelector(stopElementSelector);

            const startElementRect = startElement.getBoundingClientRect();
            const stopElementRect = stopElement.getBoundingClientRect();

            const startElementPositionValue =
                startElementPosition === "top"
                    ? startElementRect.top + window.scrollY
                    : startElementRect.bottom + window.scrollY;
            const stopElementPositionValue =
                stopElementPosition === "top"
                    ? stopElementRect.top + window.scrollY
                    : stopElementRect.bottom + window.scrollY;

            const gap = Math.abs(stopElementPositionValue - startElementPositionValue);
            setGapPixels(gap);
            setGapCalculated(true);
        };

        // Debounce function to limit the frequency of the resize event
        const debounce = function namedDebounce(func, delay) {
            let timeout;
            return function innerDebounce(...args) {
                clearTimeout(timeout);
                timeout = setTimeout(() => func.apply(this, args), delay);
            };
        };

        // Event handler for window resize
        const handleResize = debounce(() => {
            setIsVisible(typeof window !== "undefined" && window.innerWidth > 991);
            if (window.innerWidth <= 992) {
                commonSocialShareRef.current.style.height = "auto";
            } else {
                calculateGap();
            }
        }, 200);

        // Initial setup
        calculateGap();

        // Attach event listeners
        window.addEventListener("resize", handleResize);

        // Cleanup event listeners on component unmount
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, [startElementSelector, stopElementSelector, startElementPosition, stopElementPosition, gapPixels]);

    // Default site URL and other metadata
    const themeSettings = getThemeSettings();
    const slug = pageSlug || null;
    const siteUrl = getSiteURL();
    const generateSocialSharingLink = (siteurl, pageslug) => {
        let url = `${siteurl}/${pageslug}`;
        // Replace '//' with '/' if found in the middle of the URL (ignoring the protocol part)
        url = url.replace(/([^:]\/)\/+/g, "$1");
        return url;
    };
    const shareUrl = generateSocialSharingLink(siteUrl, slug);
    const shareTitle = pageTitle || "";

    // Function to copy URL to clipboard
    const copyToClipboard = () => {
        navigator.clipboard.writeText(shareUrl);
    };

    // Custom utility classes for the commonSocialShare div
    const commonSocialShareClasses = `my-5 lg:my-0 lg:sticky lg:top-26 xl:top-49.5 left-4 z-50 opacity-100 lg:mb-17.5 ${additionalClasses}`;

    const [copyLinkButtonText] = getMicrocopyValueFromKeys(themeSettings?.blogMicrocopyGroup, [
        COPY_LINK_BUTTON_TOOLTIP_TEXT,
    ]);

    return (
        gapCalculated && (
            <div
                ref={commonSocialShareRef}
                style={{ height: isVisible ? `${gapPixels}px` : "auto" }}
                className={`commonSocialShareTrack lg:absolute  z-0 h-auto lg:top-0 lg:bottom-0 lg:w-12.5 ${styles.socialShareComp}`}
            >
                <div className="relative w-full h-full">
                    <div id="commonSocialShare" className={`${commonSocialShareClasses}`}>
                        <div className="flex lg:flex-col gap-3.75">
                            <div>
                                <button
                                    type="button"
                                    aria-label="Copy to clipboard"
                                    data-tooltip-id="link-copy-tooltip"
                                    onClick={copyToClipboard}
                                    className="group bg-transparent border-none p-0 font-inherit text-inherit cursor-pointer flex justify-center items-center w-12.5 h-12.5 rounded-xs bg-white shadow-lgMedium"
                                >
                                    <CopySVG className="fill-gray-light xl:group-hover:fill-green transition-all duration-200" />
                                </button>
                                {typeof copyLinkButtonText !== "undefined" && (
                                    <ReactTooltip id="link-copy-tooltip" place="right">
                                        {copyLinkButtonText}
                                    </ReactTooltip>
                                )}
                            </div>

                            {themeSettings?.socialSharingPlatforms.includes("Linkedin") && (
                                <div>
                                    <LinkedinShareButton
                                        className="group socialsShareLinkedIn flex justify-center items-center w-12.5 h-12.5 rounded-xs bg-white shadow-lgMedium"
                                        title={shareTitle}
                                        url={shareUrl}
                                    >
                                        <LinkedinSVG className="fill-gray-light xl:group-hover:fill-green transition-all duration-200" />
                                    </LinkedinShareButton>
                                </div>
                            )}
                            {themeSettings?.socialSharingPlatforms.includes("Facebook") && (
                                <div>
                                    <FacebookShareButton
                                        className="group socialsShareFacebook flex justify-center items-center w-12.5 h-12.5 rounded-xs bg-white shadow-lgMedium"
                                        title={shareTitle}
                                        url={shareUrl}
                                    >
                                        <FacebookSVG className="fill-gray-light xl:group-hover:fill-green transition-all duration-200" />
                                    </FacebookShareButton>
                                </div>
                            )}
                            {themeSettings?.socialSharingPlatforms.includes("Twitter") && (
                                <div>
                                    <TwitterShareButton
                                        className="group socialsShareTwitter flex justify-center items-center w-12.5 h-12.5 rounded-xs bg-white shadow-lgMedium"
                                        title={shareTitle}
                                        url={shareUrl}
                                    >
                                        <TwitterSVG className="fill-gray-light xl:group-hover:fill-green transition-all duration-200" />
                                    </TwitterShareButton>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        )
    );
};

export default SocialShare;
